import { Fragment, ReactNode } from "react";

import type { StandardnessDimension, StandardnessScore } from "./standardness";

const axisLabels: Record<StandardnessDimension, string> = {
  start: "启动",
  impact: "击球",
  followThrough: "随挥",
  power: "力量",
  stability: "稳定",
  technique: "技巧",
};

const axisOrder: StandardnessDimension[] = [
  "start",
  "impact",
  "followThrough",
  "power",
  "stability",
  "technique",
];

export function ComparisonOverview({
  llmStatus,
  score,
  summary,
}: {
  llmStatus: "idle" | "pending" | "completed" | "error";
  score: StandardnessScore | null;
  summary?: string[] | null;
}) {
  return (
    <article className="result-card result-overview-card">
      <section className="overview-summary" aria-labelledby="overview-summary-title">
        <h3 id="overview-summary-title">总结</h3>
        <OverviewSummary llmStatus={llmStatus} summary={summary} />
      </section>

      <section className="standardness-score" aria-label="动作标准度">
        <StandardnessGauge score={score} />
        <span>动作标准度</span>
      </section>

      <StandardnessRadar score={score} />
    </article>
  );
}

function OverviewSummary({
  llmStatus,
  summary,
}: {
  llmStatus: "idle" | "pending" | "completed" | "error";
  summary?: string[] | null;
}) {
  if (summary?.length) {
    return (
      <div className="overview-summary-list">
        {summary.map((item, index) => (
          <Fragment key={`summary-${index}`}>
            <p><OverviewMarkdownText text={item} /></p>
            {index < summary.length - 1 ? <span aria-hidden="true" /> : null}
          </Fragment>
        ))}
      </div>
    );
  }

  if (llmStatus === "idle" || llmStatus === "pending") {
    return (
      <div className="overview-summary-state">
        <i aria-hidden="true" />
        <p>AI正在分析您的练习动作</p>
      </div>
    );
  }

  if (llmStatus === "error") {
    return (
      <div className="overview-summary-state error">
        <strong>AI 报告生成失败</strong>
        <p>报告暂未取到，请稍后重新打开结果。</p>
      </div>
    );
  }

  return <p className="overview-summary-empty">暂无 AI 分析数据</p>;
}

function StandardnessGauge({ score }: { score: StandardnessScore | null }) {
  const radius = 72;
  const circumference = 2 * Math.PI * radius;
  const progress = score ? clamp(score.value, 0, 100) / 100 : 0;

  return (
    <div
      className="standardness-gauge"
      role="img"
      aria-label={`动作标准度 ${score ? `${score.displayValue} / 100` : "— / 100"}`}
    >
      <svg viewBox="0 0 176 176" aria-hidden="true">
        <circle className="standardness-gauge-track" cx="88" cy="88" r={radius} />
        <circle
          className="standardness-gauge-value"
          cx="88"
          cy="88"
          r={radius}
          strokeDasharray={`${circumference * progress} ${circumference}`}
        />
      </svg>
      <div className="standardness-gauge-label">
        <strong>{score?.displayValue ?? "—"}</strong>
        <span>/100</span>
      </div>
    </div>
  );
}

function StandardnessRadar({ score }: { score: StandardnessScore | null }) {
  const size = 380;
  const center = size / 2;
  const plotRadius = size * 0.3;
  const labelRadius = size * 0.405;
  const values = new Map(score?.dimensions.map((item) => [item.dimension, item.value]) ?? []);
  const gridLevels = [0.2, 0.4, 0.6, 0.8];
  const outerPath = polygonPath(axisOrder.map((_, index) => polarPoint(index, 1, center, plotRadius)));
  const completeValues = axisOrder.every((axis) => finiteScore(values.get(axis)) !== null);
  const dataPoints = axisOrder.map((axis, index) => {
    const value = finiteScore(values.get(axis));
    return value === null ? null : polarPoint(index, value / 100, center, plotRadius);
  });
  const dataPath = completeValues
    ? `${polygonPath(dataPoints.filter(isPoint))} Z`
    : adjacentSegments(dataPoints);
  const accessibilityValue = axisOrder
    .map((axis) => `${axisLabels[axis]} ${formatScore(values.get(axis))}`)
    .join("，");

  return (
    <div className="standardness-radar" role="img" aria-label={`动作六维评分：${accessibilityValue}`}>
      <svg viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        {gridLevels.map((level) => (
          <path
            className="standardness-radar-grid"
            d={`${polygonPath(axisOrder.map((_, index) => polarPoint(index, level, center, plotRadius)))} Z`}
            key={level}
          />
        ))}
        <path className="standardness-radar-grid outer" d={`${outerPath} Z`} />
        {axisOrder.map((axis, index) => {
          const point = polarPoint(index, 1, center, plotRadius);
          return <line className="standardness-radar-spoke" key={axis} x1={center} x2={point[0]} y1={center} y2={point[1]} />;
        })}
        {dataPath ? <path className={`standardness-radar-data ${completeValues ? "complete" : "partial"}`} d={dataPath} /> : null}
        {dataPoints.map((point, index) => point ? (
          <circle className="standardness-radar-point" cx={point[0]} cy={point[1]} key={axisOrder[index]} r="4" />
        ) : null)}
        {axisOrder.map((axis, index) => {
          const point = polarPoint(index, 1, center, labelRadius);
          return (
            <text className="standardness-radar-label" dominantBaseline="middle" key={axis} textAnchor="middle" x={point[0]} y={point[1]}>
              {axisLabels[axis]}
            </text>
          );
        })}
      </svg>
    </div>
  );
}

function OverviewMarkdownText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<strong key={`${match.index}-${match[1]}`}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

type Point = [number, number];

function polarPoint(index: number, scale: number, center: number, radius: number): Point {
  const angle = -Math.PI / 2 + index * 2 * Math.PI / axisOrder.length;
  return [
    center + Math.cos(angle) * radius * scale,
    center + Math.sin(angle) * radius * scale,
  ];
}

function polygonPath(points: Point[]): string {
  return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point[0].toFixed(2)} ${point[1].toFixed(2)}`).join(" ");
}

function adjacentSegments(points: Array<Point | null>): string {
  return points.map((point, index) => {
    const next = points[(index + 1) % points.length];
    if (!point || !next) return "";
    return `M ${point[0].toFixed(2)} ${point[1].toFixed(2)} L ${next[0].toFixed(2)} ${next[1].toFixed(2)}`;
  }).filter(Boolean).join(" ");
}

function isPoint(value: Point | null): value is Point {
  return value !== null;
}

function finiteScore(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value)
    ? clamp(value, 0, 100)
    : null;
}

function formatScore(value: number | null | undefined): string {
  const score = finiteScore(value);
  return score === null ? "暂无数据" : String(Math.round(score));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
